<script setup lang="ts">
import {ref, computed, watch, onMounted, onUnmounted, nextTick} from 'vue';
import {PRIVATE_CAPTCHA_SITE_KEY} from '@/ts/utils/consts';
import {usePrivateCaptcha} from '@/ts/composables/stateful/use-private-captcha';
import {useUserPreferencesStore, ColorScheme} from '@/ts/store/user-preferences.store';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useLang} from '@/ts/composables/stateful/use-lang';

const props = defineProps<{
  // Defaults to the registration site key; pass a different one to track the
  // widget under its own Private Captcha property (e.g. resend verification).
  sitekey?: string;
  // The dialog embedding (verify-email resend) provides its own heading.
  hideTitle?: boolean;
}>();

const emit = defineEmits<{
  loading: [boolean],
  scriptFailed: [boolean],
}>();

const effectiveSitekey = computed(() => props.sitekey ?? PRIVATE_CAPTCHA_SITE_KEY);
const isCaptchaActive = computed(() => Boolean(effectiveSitekey.value));

const {colorScheme} = useUserPreferencesStore();
const {lang} = useLang();

// Track system color scheme changes for adaptive mode (reactive for Chrome DevTools emulation)
const systemPrefersDark = ref(window.matchMedia('(prefers-color-scheme: dark)').matches);

const privateCaptchaTheme = computed(() => {
  if (colorScheme === ColorScheme.dark) {
    return 'dark';
  }
  if (colorScheme === ColorScheme.light) {
    return 'light';
  }
  // For adaptive, use reactive system preference
  return systemPrefersDark.value ? 'dark' : 'light';
});

// Custom styles for captcha widget
const captchaCustomStyles = computed(() => {
  return `
    display: block;
    font-size: clamp(14px, 2.5vw, 16px);
    --border-radius: var(--card-border-radius);
    --border: 1px solid var(--Neutral-UI-neutral-ui-20);
    --background-color: var(--Neutral-UI-neutral-ui-0);
    --checkbox-background-color: var(--Neutral-UI-neutral-ui-0);
    --dark-color: var(--Neutral-UI-neutral-ui-100);
    --accent-color: var(--Primary-primary-color-1);
    --pie-color: var(--Primary-primary-color-2);
    --checkbox-hover-color: var(--Primary-primary-color-1);
    --extra-spacing: var(--Spacing-spacing-4);
  `.trim();
});

const {isCaptchaLoading, captchaToken, resetCaptcha, waitForScriptLoad, renderWidget} = usePrivateCaptcha(effectiveSitekey.value);

// Emit loading state changes to parent
watch(isCaptchaLoading, (isLoading) => {
  emit('loading', isLoading);
});

// Ref to the captcha widget container
const captchaWidgetRef = ref<HTMLElement | null>(null);

// Key to force widget recreation on theme/disabled change
const widgetKey = ref(0);

const logger = useLogger('sign-up-captcha');

// Generation counter to make stale async widget mounts a no-op when the
// disabled flag flips faster than `waitForScriptLoad` resolves.
let widgetGeneration = 0;

const mountWidget = (): void => {
  if (!captchaWidgetRef.value || !window.privateCaptcha?.render) {
    return;
  }
  renderWidget(captchaWidgetRef.value, {
    sitekey: effectiveSitekey.value!,
    theme: privateCaptchaTheme.value,
    lang: lang.value,
    displayMode: 'widget',
    startMode: 'click',
    styles: captchaCustomStyles.value,
  });
};

const remountWidget = async (): Promise<void> => {
  if (!isCaptchaActive.value) {
    return;
  }
  const generation = ++widgetGeneration;
  // The CDN script failing to load (offline, blocker...) is surfaced to the
  // parent, which renders an inline error + retry — without the widget the
  // flow cannot continue.
  emit('scriptFailed', false);
  try {
    await waitForScriptLoad();
  } catch (error) {
    logger.error('Private Captcha script failed to load:', error)();
    emit('scriptFailed', true);
    return;
  }
  widgetKey.value++;
  await nextTick();
  if (generation !== widgetGeneration) {
    return;
  }
  mountWidget();
};

// Listen to system color scheme changes (for Chrome DevTools emulation)
let mediaQuery: MediaQueryList | null = null;
const handleSystemThemeChange = (e: MediaQueryListEvent): void => {
  systemPrefersDark.value = e.matches;
};

onMounted(async () => {
  // Listen for system color scheme changes
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', handleSystemThemeChange);

  // Render captcha widget after script loads
  await remountWidget();
});

onUnmounted(() => {
  if (mediaQuery) {
    mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }
  // The captcha state is shared module-level; without this a solved (single-use)
  // token would survive unmount and be replayed on the next mount (e.g. after
  // navigating Back from the captcha step, or when the resend dialog is
  // reopened).
  resetCaptcha();
});

// Re-render the widget when the theme or the app language changes (the shell's
// language switcher swaps locale without a full reload, so the widget must
// re-render to pick up the new language).
watch([privateCaptchaTheme, lang], async () => {
  if (!isCaptchaActive.value) {
    return;
  }
  resetCaptcha();
  await remountWidget();
});

defineExpose({
  isCaptchaActive,
  captchaToken,
  resetAndRemount: async (): Promise<void> => {
    resetCaptcha();
    await remountWidget();
  },
});
</script>
<template>
  <div class="sign-up-captcha">
    <h1 v-if="!hideTitle">
      {{ $t('verify_youre_human') }}
    </h1>
    <div v-if="isCaptchaActive" class="captcha-container">
      <div ref="captchaWidgetRef" :key="widgetKey" class="private-captcha"/>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.sign-up-captcha {
  display: flex;
  flex-direction: column;
}

h1 {
  @include font-display-4;
  margin-bottom: var(--Spacing-spacing-6);
}

// No margin-top: the step title's own spacing-6 margin already provides the
// title-to-content gap (a margin here stacked on top of it).
.captcha-container {
  margin-bottom: var(--Spacing-spacing-4);
  min-height: 80px;

  @include screen-lg {
    margin-bottom: var(--Spacing-spacing-5);
  }
}

.private-captcha {
  display: block;
  width: 100%;
  height: 78px;
  border-radius: var(--Border-Radius-3);

  // Hide the Private Captcha logo
  :deep(.pc-logo),
  :deep(.pc-branding),
  :deep(a[href*="privatecaptcha"]) {
    display: none !important;
  }

  :deep(iframe) {
    width: 100%;
    height: 100%;
  }
}
</style>
